import { useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import { MfaPanel } from './mfa-panel'
import { api } from '../lib/api'
import { useEntryCount } from '../lib/team-data'
import { Toggle } from '../components/ui'
import { BackupPanel } from '../components/BackupPanel'
import { ACCENTS, ROLE_LABEL, type Appearance, type Settings } from '../lib/types'

const RULES: ReadonlyArray<readonly [keyof Settings, string, string]> = [
  [
    'requireProject',
    'Require a project',
    'No time is logged without one — not by the timer, and not by the entry form.',
  ],
  [
    'requireClient',
    'Require a client',
    'The project picked has to belong to a client. Time attaches to a project, so this is how a client is required.',
  ],
  ['autoStop', 'Stop timers at midnight', 'Prevents a forgotten timer running all night.'],
  ['roundToMinute', 'Round to the nearest minute', 'Applied when a timer is stopped.'],
  ['allowManual', 'Allow manual entry', 'Turn off to hide the manual range fields on the timer.'],
]

export function SettingsScreen() {
  const { ws, me, act, canManage, confirm, user, auth, selectWorkspace, createWorkspace, joinWorkspace, setModal, flash } =
    useWorkspace()
  const S = ws.settings
  const A = ws.appearance
  const isOwner = me.role === 'owner'

  const [name, setName] = useState(ws.name)
  const [tagDraft, setTagDraft] = useState('')
  const [newWs, setNewWs] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [gateErr, setGateErr] = useState('')

  const patchSettings = (patch: Partial<Settings>) => void act.updateWorkspace({ settings: patch })
  const patchAppearance = (patch: Partial<Appearance>) => void act.updateAppearance(patch)

  // The snapshot is your own time, so this is already just yours.
  const myEntryCount = ws.entries.length

  // The workspace-wide figure is not in the snapshot any more; it is one
  // aggregate, asked for only by the people who can see the delete controls.
  const allEntryCount = useEntryCount(ws.id, canManage)
  const allEntryText = allEntryCount === null ? 'every' : `all ${allEntryCount}`

  /**
   * Ends every session on every device, not just this browser -- the point of
   * the control is a lost laptop, where signing out locally achieves nothing.
   */
  const signOutEverywhere = async () => {
    try {
      await api.logoutEverywhere()
      flash('Signed out on every device')
      window.location.replace('/')
    } catch {
      flash('Could not sign out everywhere.')
    }
  }

  return (
    <section className="settings-grid">
      {/* ---------- workspace ---------- */}
      {canManage ? (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card-title">Workspace</div>
          <div>
            <label className="label" htmlFor="ws-name">
              Name
            </label>
            <input
              id="ws-name"
              className="input input--canvas"
              style={{ padding: '9px 11px' }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() && name !== ws.name && void act.updateWorkspace({ name: name.trim() })}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label className="label" htmlFor="ws-weekstart">
                Week starts
              </label>
              <select
                id="ws-weekstart"
                className="select"
                style={{ padding: '9px 11px' }}
                value={S.weekStart}
                onChange={(e) => patchSettings({ weekStart: e.target.value as Settings['weekStart'] })}
              >
                <option value="Monday">Monday</option>
                <option value="Sunday">Sunday</option>
              </select>
            </div>
            <div>
              <label className="label" htmlFor="ws-timefmt">
                Time format
              </label>
              <select
                id="ws-timefmt"
                className="select"
                style={{ padding: '9px 11px' }}
                value={S.timeFormat}
                onChange={(e) => patchSettings({ timeFormat: e.target.value as Settings['timeFormat'] })}
              >
                <option value="12h">12-hour</option>
                <option value="24h">24-hour</option>
              </select>
            </div>
          </div>
          <div>
            <span className="label">Duration display</span>
            <div style={{ display: 'flex', gap: 6 }} role="group" aria-label="Duration display">
              {(
                [
                  ['h:mm', '1:30'],
                  ['decimal', '1.50h'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className="chip chip--ac mono"
                  style={{ flex: 1, textAlign: 'center', padding: 9 }}
                  aria-pressed={S.durFormat === id}
                  onClick={() => patchSettings({ durFormat: id })}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="card-title">Workspace</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{ws.name}</div>
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: 0 }}>
            You're a {ROLE_LABEL[me.role]} here. Week start, time format and tracking rules are set by the owner and
            apply to everyone.
          </p>
        </div>
      )}

      {/* ---------- tracking rules ---------- */}
      {canManage && (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>
            Tracking rules
          </div>
          {RULES.map(([key, label, hint]) => (
            <div key={key} className="setting-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="setting-title">{label}</div>
                <p className="pretty">{hint}</p>
              </div>
              <Toggle
                checked={S[key] as boolean}
                label={label}
                onChange={(v) => patchSettings({ [key]: v } as Partial<Settings>)}
              />
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>
            Joining
          </div>
          <div className="setting-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="setting-title">Allow people to request access</div>
              <p className="pretty">
                Anyone who signs up lands in “Waiting for approval” on the Team screen and sees
                nothing until you let them in. With this off, people can only join by invitation —
                a sign-up creates an account that reaches no workspace at all.
              </p>
            </div>
            <Toggle
              checked={ws.acceptsSignups}
              label="Allow people to request access"
              onChange={(v) => void act.updateWorkspace({ acceptsSignups: v })}
            />
          </div>
        </div>
      )}

      {/* ---------- appearance (personal) ---------- */}
      <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div className="card-title">Appearance</div>
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '4px 0 0' }}>
            Yours alone — teammates keep their own.
          </p>
        </div>

        {(
          [
            ['navLayout', 'Navigation', [['sidebar', 'Sidebar'], ['topnav', 'Top bar']]],
            ['timerStyle', 'Timer style', [['bar', 'Compact bar'], ['card', 'Big clock']]],
            ['reportsChart', 'Reports chart', [['bars', 'Bars'], ['line', 'Line']]],
          ] as const
        ).map(([key, label, options]) => (
          <div key={key}>
            <span className="label">{label}</span>
            <div className="seg" style={{ width: '100%' }}>
              {options.map(([value, text]) => (
                <button
                  key={value}
                  type="button"
                  style={{ flex: 1 }}
                  aria-pressed={A[key] === value}
                  onClick={() => patchAppearance({ [key]: value } as Partial<Appearance>)}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div>
          <span className="label">Accent colour</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="radiogroup" aria-label="Accent colour">
            {ACCENTS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={A.accent === c}
                aria-label={c}
                className="swatch"
                style={{
                  background: c,
                  boxShadow: A.accent === c ? `0 0 0 2px var(--surf), 0 0 0 4px ${c}` : 'none',
                }}
                onClick={() => patchAppearance({ accent: c })}
              />
            ))}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
          <div className="card-title" style={{ marginBottom: 6 }}>
            Your account
          </div>
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
            Signed in as <b>{user?.email}</b>.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn" onClick={() => setModal({ kind: 'profile' })}>
              Name and password
            </button>
            <button type="button" className="btn" onClick={() => void signOutEverywhere()}>
              Sign out everywhere
            </button>
          </div>
        </div>
      </div>

      {/* ---------- two-factor ---------- */}
      <MfaPanel />

      {/* ---------- tags ---------- */}
      {canManage && (
        <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card-title">Tags</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {ws.tags.map((t) => (
              <div key={t} className="tag-chip">
                {t}
                <button type="button" aria-label={`Remove tag ${t}`} onClick={() => void act.removeTag(t)}>
                  ×
                </button>
              </div>
            ))}
            {!ws.tags.length && <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>No tags yet.</div>}
          </div>
          <input
            className="input input--canvas"
            style={{ padding: '9px 11px' }}
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && tagDraft.trim()) {
                e.preventDefault()
                void act.addTag(tagDraft.trim())
                setTagDraft('')
              }
            }}
            placeholder="Add a tag and press Enter"
            aria-label="Add a tag"
          />
        </div>
      )}

      {/* ---------- other workspaces ---------- */}
      <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card-title">Your workspaces</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(auth?.workspaces ?? []).map((w) => (
            <button
              key={w.id}
              type="button"
              className="popover-row"
              aria-selected={w.id === ws.id}
              style={{ border: '1px solid var(--line)' }}
              onClick={() => w.id !== ws.id && selectWorkspace(w.id)}
            >
              <span className="trunc" style={{ flex: 1 }}>
                {w.name}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>
                {w.id === ws.id ? 'Current' : ROLE_LABEL[w.role]}
              </span>
            </button>
          ))}
        </div>

        <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="label" style={{ marginBottom: 0 }}>
            Create another
          </span>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input input--canvas"
              style={{ padding: '9px 11px' }}
              value={newWs}
              onChange={(e) => {
                setNewWs(e.target.value)
                setGateErr('')
              }}
              placeholder="New workspace name"
              aria-label="New workspace name"
            />
            <button
              type="button"
              className="btn"
              disabled={!newWs.trim()}
              onClick={() =>
                void createWorkspace(newWs.trim()).then((e) => {
                  if (e) setGateErr(e)
                  else setNewWs('')
                })
              }
            >
              Create
            </button>
          </div>

          <span className="label" style={{ marginBottom: 0, marginTop: 6 }}>
            Join with a code
          </span>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input input--canvas mono"
              style={{ padding: '9px 11px', letterSpacing: '.08em' }}
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value.toUpperCase())
                setGateErr('')
              }}
              placeholder="ABCD-2345"
              aria-label="Join code"
            />
            <button
              type="button"
              className="btn"
              disabled={!joinCode.trim()}
              onClick={() =>
                void joinWorkspace(joinCode.trim()).then((e) => {
                  if (e) setGateErr(e)
                  else setJoinCode('')
                })
              }
            >
              Join
            </button>
          </div>
          {gateErr && <div className="auth-err" style={{ marginBottom: 0 }}>{gateErr}</div>}
        </div>
      </div>

      {/* Above the danger zone, not inside it: taking a copy of the workspace
          is the opposite of destroying it, and the restore here cannot delete
          anything. Gated for disclosure, not for danger -- canManage is owner
          and admin only, deliberately not manager, which the server check
          mirrors with workspace_role rather than is_admin(). */}
      {canManage && <BackupPanel />}

      {/* ---------- danger zone ----------

          Owner only. Wiping time and deleting the workspace are the owner's
          calls, not something an admin or manager can do on their own.
          "Leave workspace" is deliberately NOT in here -- see below. */}
      {isOwner && (
      <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="card-title" style={{ color: 'var(--dg)' }}>
          Danger zone
        </div>

        <div>
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
            Clears the {myEntryCount} time {myEntryCount === 1 ? 'entry' : 'entries'} you logged in this workspace.
            Nobody else's time is touched.
          </p>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() =>
              confirm({
                title: 'Delete your time entries?',
                body: `All ${myEntryCount} of your entries in ${ws.name} will be removed. This can't be undone — export from Reports first if you might want them.`,
                cta: 'Delete my entries',
                danger: true,
                onConfirm: () => void act.wipeEntries('mine'),
              })
            }
          >
            Delete my time entries
          </button>
        </div>

        {canManage && (
          <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
            <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
              Clears every time entry in this workspace, for everyone. Projects, clients and people stay.
            </p>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() =>
                confirm({
                  title: 'Delete everyone’s time entries?',
                  body: `This removes ${allEntryText} entries in ${ws.name}, for every member. It can't be undone.`,
                  cta: 'Delete all entries',
                  danger: true,
                  onConfirm: () => void act.wipeEntries('all'),
                })
              }
            >
              Delete all time entries
            </button>
          </div>
        )}

        {isOwner && (
          <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
            <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
              Permanently delete <b>{ws.name}</b> for everyone in it, including all projects, clients and time.
            </p>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() =>
                confirm({
                  title: 'Delete this workspace?',
                  body: `${ws.name}, its ${allEntryCount ?? 'stored'} time entries and everyone's access will be permanently removed. Their accounts stay. This can't be undone.`,
                  cta: 'Delete workspace',
                  danger: true,
                  onConfirm: () => void act.deleteWorkspace(),
                })
              }
            >
              Delete workspace
            </button>
          </div>
        )}
      </div>
      )}

      {/* Leaving is not a danger-zone power. Gating it with the card above
          left nobody able to leave at all: the owner has no such button, and
          every other role sat inside the owner test. */}
      {!isOwner && (
        <div className="card card--pad">
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
            Leave {ws.name}. Your time stays with the workspace; you keep your account.
          </p>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() =>
              confirm({
                title: `Leave ${ws.name}?`,
                body: 'You lose access until someone invites you back or gives you the join code again.',
                cta: 'Leave workspace',
                danger: true,
                onConfirm: () => void act.leaveWorkspace(),
              })
            }
          >
            Leave workspace
          </button>
        </div>
      )}
    </section>
  )
}
